import React, { useState, useEffect } from 'react';
import {
    Grid,
    Box,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton
} from '@mui/material';
import { Delete, Edit, Add } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useTheme } from '@mui/system';
import moment from 'moment';
import ApiHelper from 'utils/apiHelper';
import AddSchemeDialog from './AddSchemeDialog';
import AddScheduledEventDialog from './AddScheduledEventDialog';
import EditSchemeDialog from './EditSchemeDialog';
import EditScheduledEventDialog from './EditScheduledEventDialog';

const EditPolicyDialog = ({ onClose, policyData }) => {
    const theme = useTheme();
    const [policyName, setPolicyName] = useState(policyData.name);
    const [policyDescription, setPolicyDescription] = useState(policyData.description);
    const [showAddSchemeDialog, setShowAddSchemeDialog] = useState(false);
    const [showEditSchemeDialog, setShowEditSchemeDialog] = useState(false);
    const [showAddScheduledEventDialog, setShowAddScheduledEventDialog] = useState(false);
    const [showEditScheduledEventDialog, setShowEditScheduledEventDialog] = useState(false);
    const [schemeData, setSchemeData] = useState('');
    const [scheduledEventsData, setScheduledEventsData] = useState('');
    const [selectedSchemeData, setSelectedSchemeData] = useState('');
    const [selectedScheduledEventData, setSelectedScheduledEventData] = useState('');
    const [selectedSchemeIndex, setSelectedSchemeIndex] = useState();
    const [selectedScheduledEventIndex, setSelectedScheduledEventIndex] = useState();

    useEffect(() => {
        getInitialData();
    }, []);

    const getInitialData = () => {
        ApiHelper.get(`/api/management/policy/${policyData.policyid}/`)
            .then((res) => {
                localStorage.setItem('policyScheduledEventsData', JSON.stringify(res.data.scheduled_events));
                localStorage.setItem('policySchemeData', JSON.stringify(res.data.schemes));
                setSchemeData(JSON.parse(localStorage.getItem('policySchemeData')));
                setScheduledEventsData(JSON.parse(localStorage.getItem('policyScheduledEventsData')));
            })
            .catch(() => {
                toast.error('Failed to get the policy data.');
            });
    };

    const getData = () => {
        setSchemeData(JSON.parse(localStorage.getItem('policySchemeData')));
        setScheduledEventsData(JSON.parse(localStorage.getItem('policyScheduledEventsData')));
    };

    const handleClose = (event, reason) => {
        if (reason && reason === 'backdropClick') return;
        localStorage.removeItem('policySchemeData');
        localStorage.removeItem('policyScheduledEventsData');
        onClose();
    };

    const handleDays = (days) => {
        const dayKeys = Object.keys(days);
        let selectedDays = '';
        for (let i = 0; i < dayKeys.length; i++) {
            if (days[dayKeys[i]] === true) selectedDays += dayKeys[i].toUpperCase()[0];
        }
        return selectedDays;
    };

    const handleAddSchemeData = () => {
        setShowAddSchemeDialog(false);
        getData();
    };

    const handleAddScheduledEventData = () => {
        setShowAddScheduledEventDialog(false);
        getData();
    };

    const handleCloseEditScheduledEventDialog = () => {
        setShowEditScheduledEventDialog(false);
        getData();
    };

    const handleCloseEditSchemeDialog = () => {
        setShowEditSchemeDialog(false);
        getData();
    };

    const handleEditScheme = (data, key) => {
        setShowEditSchemeDialog(true);
        setSelectedSchemeData(data);
        setSelectedSchemeIndex(key);
    };

    const handleEditScheduledEvent = (data, key) => {
        setShowEditScheduledEventDialog(true);
        setSelectedScheduledEventData(data);
        setSelectedScheduledEventIndex(key);
    };

    const handleDelete = (key, name, data) => {
        if (name === 'scheme') {
            if (data.schemeName === 'Background Scheme') {
                toast.warning(`Warning! You can't delete this default scheme.`);
                return true;
            } else {
                schemeData.splice(key, 1);
                localStorage.removeItem('policySchemeData');
                localStorage.setItem('policySchemeData', JSON.stringify(schemeData));
            }
        } else {
            scheduledEventsData.splice(key, 1);
            localStorage.removeItem('policyScheduledEventsData');
            localStorage.setItem('policyScheduledEventsData', JSON.stringify(scheduledEventsData));
        }
        getData();
    };

    const handleSubmit = () => {
        if (schemeData.length === 0) {
            toast.warning('Please add at least one scheme.');
            return true;
        }
        ApiHelper.put(`/api/management/policy/${policyData.policyid}/`, {
            policy_name: policyName,
            policy_description: policyDescription,
            schemes: schemeData ? schemeData : [],
            scheduled_events: scheduledEventsData ? scheduledEventsData : []
        })
            .then(() => {
                toast.success('Successfully updated.');
                handleClose();
            })
            .catch((err) => {
                toast.error(`Fail! ${err.response.data.message}`);
            });
    };

    if (!scheduledEventsData && !schemeData) {
        return null;
    } else {
        return (
            <Dialog open={true} onClose={handleClose}>
                <DialogTitle sx={{ fontSize: '16px' }}>Edit Policy</DialogTitle>
                <DialogContent sx={{ minWidth: '600px' }}>
                    <Box sx={{ mb: 3, mt: 3 }}>
                        <TextField
                            sx={{ width: '100%' }}
                            label="Policy Name"
                            value={policyName}
                            onChange={(e) => setPolicyName(e.target.value)}
                        />
                    </Box>
                    <Box sx={{ mb: 3, mt: 3 }}>
                        <TextField
                            sx={{ width: '100%' }}
                            label="Policy Description"
                            value={policyDescription}
                            onChange={(e) => setPolicyDescription(e.target.value)}
                        />
                    </Box>
                    <Box sx={{ border: `1px solid ${theme.palette.primary.main}` }}>
                        <Grid
                            container
                            item
                            sx={{
                                background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary[200]})`,
                                padding: 1
                            }}
                        >
                            <Grid item sm={9} md={9}>
                                <Typography sx={{ color: '#FFFFFF', fontSize: '16px', marginTop: 1 }}>Schemes</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Button
                                    sx={{ float: 'right', backgroundColor: '#FFFFFF', borderRadius: '6px' }}
                                    onClick={() => setShowAddSchemeDialog(true)}
                                    startIcon={<Add />}
                                >
                                    Add Scheme
                                </Button>
                                {showAddSchemeDialog && <AddSchemeDialog onClose={handleAddSchemeData} />}
                            </Grid>
                        </Grid>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Days</TableCell>
                                    <TableCell>Start</TableCell>
                                    <TableCell>End</TableCell>
                                    <TableCell align="right">Options</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {schemeData &&
                                    schemeData.length > 0 &&
                                    schemeData.map((data, key) => (
                                        <TableRow key={key}>
                                            <TableCell>{data.schemeName}</TableCell>
                                            <TableCell>{handleDays(data.days)}</TableCell>
                                            <TableCell>{moment(data.startTime, 'HH:mm:ss').format('hh:mm:ss a')}</TableCell>
                                            <TableCell>{moment(data.endTime, 'HH:mm:ss').format('hh:mm:ss a')}</TableCell>
                                            <TableCell align="right">
                                                <Box>
                                                    <IconButton size="small" onClick={() => handleEditScheme(data, key)}>
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                    <IconButton size="small" onClick={() => handleDelete(key, 'scheme', data)}>
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                {showEditSchemeDialog && (
                                    <EditSchemeDialog
                                        data={selectedSchemeData}
                                        index={selectedSchemeIndex}
                                        onClose={handleCloseEditSchemeDialog}
                                    />
                                )}
                            </TableBody>
                        </Table>
                    </Box>
                    <Box sx={{ mt: 3, border: `1px solid ${theme.palette.secondary.main}` }}>
                        <Grid
                            container
                            item
                            sx={{
                                background: `linear-gradient(to right, ${theme.palette.secondary.main}, ${theme.palette.secondary[200]})`,
                                padding: 1
                            }}
                        >
                            <Grid item sm={9} md={9}>
                                <Typography sx={{ color: '#FFFFFF', fontSize: '16px', marginTop: 1 }}>Scheduled Events</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Button
                                    sx={{ float: 'right', backgroundColor: '#FFFFFF', borderRadius: '6px' }}
                                    onClick={() => setShowAddScheduledEventDialog(true)}
                                    startIcon={<Add />}
                                >
                                    Add Event
                                </Button>
                                {showAddScheduledEventDialog && <AddScheduledEventDialog onClose={handleAddScheduledEventData} />}
                            </Grid>
                        </Grid>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Days</TableCell>
                                    <TableCell>Start</TableCell>
                                    <TableCell align="right">Options</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {scheduledEventsData &&
                                    scheduledEventsData.length > 0 &&
                                    scheduledEventsData.map((data, key) => (
                                        <TableRow key={key}>
                                            <TableCell>{data.eventType[0].toUpperCase() + data.eventType.slice(1)}</TableCell>
                                            <TableCell>{handleDays(data.days)}</TableCell>
                                            <TableCell>{moment(data.startTime, 'HH:mm:ss').format('hh:mm:ss a')}</TableCell>
                                            <TableCell align="right">
                                                <Box>
                                                    <IconButton size="small" onClick={() => handleEditScheduledEvent(data, key)}>
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                    <IconButton size="small" onClick={() => handleDelete(key, 'scheduledEvents')}>
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                {showEditScheduledEventDialog && (
                                    <EditScheduledEventDialog
                                        data={selectedScheduledEventData}
                                        index={selectedScheduledEventIndex}
                                        onClose={handleCloseEditScheduledEventDialog}
                                    />
                                )}
                            </TableBody>
                        </Table>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button disabled={policyName === ''} onClick={handleSubmit}>
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
};

EditPolicyDialog.propTypes = {
    onClose: PropTypes.func,
    policyData: PropTypes.object
};

export default EditPolicyDialog;
